"use client";
import { useEffect, useMemo, useState } from "react";

export function isSvgPath(path: string | null): boolean {
  return Boolean(path && /\.svg$/i.test(path));
}

export function isRasterImagePath(path: string | null): boolean {
  return Boolean(
    path &&
      /\.(png|apng|jpe?g|gif|bmp|webp|avif|ico|tiff?|heic|heif|cur)$/i.test(
        path,
      ),
  );
}

export function getImageMimeFromPath(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "png":
      return "image/png";
    case "apng":
      return "image/apng";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "bmp":
      return "image/bmp";
    case "webp":
      return "image/webp";
    case "avif":
      return "image/avif";
    case "ico":
      return "image/x-icon";
    case "cur":
      return "image/x-icon";
    case "tif":
    case "tiff":
      return "image/tiff";
    case "heic":
    case "heif":
      return "image/heic";
    case "svg":
      return "image/svg+xml";
    default:
      return "application/octet-stream";
  }
}

export function isProbablyBase64(input: string): boolean {
  const str = input.replace(/\s/g, "");
  if (!str || str.length % 4 !== 0) return false;
  return /^[A-Za-z0-9+/]+=*$/.test(str);
}

export function buildImagePreviewSrc(
  path: string | null,
  content: string,
): string {
  if (!path) return "";

  const trimmed = (content ?? "").trim();
  if (trimmed.startsWith("data:")) return trimmed;

  if (isSvgPath(path)) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(content)}`;
  }

  if (isRasterImagePath(path)) {
    const mime = getImageMimeFromPath(path);
    const raw = content ?? "";
    const compact = raw.replace(/\s/g, "");
    const base64 = isProbablyBase64(compact)
      ? compact
      : (() => {
          try {
            return btoa(unescape(encodeURIComponent(raw)));
          } catch {
            return "";
          }
        })();
    return base64 ? `data:${mime};base64,${base64}` : "";
  }

  return "";
}

export function isImagePath(path: string | null): boolean {
  return isSvgPath(path) || isRasterImagePath(path);
}

export function ImagePreview({
  path,
  content,
  className,
}: {
  path: string | null;
  content: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const src = useMemo(
    () => buildImagePreviewSrc(path, content),
    [path, content],
  );

  useEffect(() => {
    setFailed(false);
  }, [path, src]);

  const baseClass =
    "relative h-full w-full flex items-center justify-center bg-background";
  const containerClass = className ? `${baseClass} ${className}` : baseClass;

  return (
    <div className={containerClass}>
      {src && !failed ? (
        <img
          src={src}
          alt={path ?? "image preview"}
          className="object-contain max-w-[80%] max-h-[80%]"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="p-4 text-sm text-muted-foreground">
          Preview unavailable for this file content.
        </div>
      )}
    </div>
  );
}
